import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { firebaseAuth, firebaseEnabled, googleAuthProvider } from '../firebase';

const EMAIL_LINK_STORAGE_KEY = 'needradar_email_for_sign_in';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  enabled: boolean;
  error: string | null;
  loginWithGoogle: () => Promise<void>;
  sendEmailLink: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(firebaseEnabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const auth = firebaseAuth;
    if (!auth || !isSignInWithEmailLink(auth, window.location.href)) {
      return;
    }

    const completeEmailLinkSignIn = async () => {
      const storedEmail = window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY);
      if (!storedEmail) {
        setError('メールリンクの確認に必要なメールアドレスが見つかりません。もう一度リンクを送信してください。');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        await signInWithEmailLink(auth, storedEmail, window.location.href);
        window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
        window.history.replaceState({}, document.title, `${window.location.origin}${window.location.pathname}`);
      } catch (emailLinkError) {
        console.error('Email link login failed:', emailLinkError);
        setError(emailLinkError instanceof Error ? emailLinkError.message : 'メールリンクログインに失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    void completeEmailLinkSignIn();
  }, []);

  useEffect(() => {
    if (!firebaseAuth) {
      setLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setError('認証確認に時間がかかっています。再読み込みするか、もう一度お試しください。');
      setLoading(false);
    }, 8000);

    const unsubscribe = onAuthStateChanged(firebaseAuth, (nextUser) => {
      window.clearTimeout(timeoutId);
      setUser(nextUser);
      setLoading(false);
    }, (authError) => {
      window.clearTimeout(timeoutId);
      console.error('Firebase auth state check failed:', authError);
      setError(authError.message || '認証状態を確認できませんでした。');
      setLoading(false);
    });

    return () => {
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    enabled: firebaseEnabled,
    error,
    loginWithGoogle: async () => {
      if (!firebaseAuth) {
        setError('Firebase が設定されていません。');
        return;
      }

      setError(null);
      try {
        await signInWithPopup(firebaseAuth, googleAuthProvider);
      } catch (loginError) {
        console.error('Google login failed:', loginError);
        setError(loginError instanceof Error ? loginError.message : 'Googleログインに失敗しました。');
      }
    },
    sendEmailLink: async (rawEmail: string) => {
      if (!firebaseAuth) {
        const message = 'Firebase が設定されていません。';
        setError(message);
        throw new Error(message);
      }

      const email = rawEmail.trim();
      if (!email) {
        const message = 'メールアドレスを入力してください。';
        setError(message);
        throw new Error(message);
      }

      setError(null);
      try {
        await sendSignInLinkToEmail(firebaseAuth, email, {
          url: `${window.location.origin}/`,
          handleCodeInApp: true,
        });
        window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, email);
      } catch (emailLinkError) {
        console.error('Email link send failed:', emailLinkError);
        const message = emailLinkError instanceof Error ? emailLinkError.message : 'ログインリンクの送信に失敗しました。';
        setError(message);
        throw new Error(message);
      }
    },
    logout: async () => {
      if (!firebaseAuth) return;
      setError(null);
      await signOut(firebaseAuth);
    },
    getIdToken: async () => user?.getIdToken() || null,
  }), [error, loading, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
};
